import { createContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { applyUserAccessOverride } from 'utils/accessControl';

export const AuthContext = createContext(null);

const getStoredUser = () => {
  try {
    // Determine which storage is active based on where the token is
    const hasLocalToken = !!localStorage.getItem('token');
    const hasSessionToken = !!sessionStorage.getItem('token');
    
    // Prioritize the storage that has a user object AND a token, defaulting to local
    let raw = null;
    if (hasSessionToken && !hasLocalToken) {
      raw = sessionStorage.getItem('user');
    } else {
      raw = localStorage.getItem('user') || sessionStorage.getItem('user');
    }
    
    if (!raw) return null;
    const userObj = applyUserAccessOverride(JSON.parse(raw));
    
    const derivedName = userObj.full_name || userObj.name || [userObj.first_name, userObj.last_name].filter(Boolean).join(" ") || userObj.username || "";
    if (derivedName) {
      userObj.full_name = derivedName;
    }
    
    return userObj;
  } catch (err) {
    console.error('Failed to parse stored user:', err);
    return null;
  }
};

export const AuthProvider = ({ children }) => {

  const [isLoggedIn, setIsLoggedIn] =
    useState(false);

  const [isInitialized, setIsInitialized] =
    useState(false);

  const [user, setUser] =
    useState(null);

  useEffect(() => {

    const initAuth = async () => {
      // Prioritize sessionStorage if it has a token, otherwise fallback to localStorage
      // This prevents old localStorage tokens from overriding a fresh sessionStorage login
      const sessionToken = sessionStorage.getItem('token');
      const localToken = localStorage.getItem('token');
      const token = sessionToken || localToken;
      
      if (token) {
        setIsLoggedIn(true);
        const stored = getStoredUser();
        setUser(stored);

        // Background sync to fetch fresh user profile, permissions, and latest menus from DB
        try {
          const api = (await import('services/api')).default;
          const res = await api.get('/users/profile', { skipAuthRedirect: true, skipToast: true });
          const freshUser = res?.data?.data?.user;
          if (freshUser && freshUser.id) {
            const storage = localToken ? localStorage : sessionStorage;
            storage.setItem('user', JSON.stringify(freshUser));
            setUser(freshUser);
          }
        } catch {
          // Fallback gracefully if offline or network error
        }
      } else {
        setIsLoggedIn(false);
        setUser(null);
      }
      setIsInitialized(true);
    };

    initAuth();

  }, []);

  // =========================
  // LOGIN
  // =========================

  const login = (data, keepSignedIn = false) => {
    console.log('LOGIN DATA:', data);

    const storage = keepSignedIn ? localStorage : sessionStorage;
    const altStorage = keepSignedIn ? sessionStorage : localStorage;

    // Clear alternate storage so there's no conflict
    ['token', 'userId', 'user', 'full_name', 'email', 'role'].forEach(key => {
      altStorage.removeItem(key);
    });

    // SAVE TOKEN
    storage.setItem('token', data?.token || '');

    // FIXED USER EXTRACTION
    const userData = data?.user || data?.data || {};
    
    // Ensure permissions array is attached to the user object!
    if (data?.permissions && !userData.permissions) {
      userData.permissions = data.permissions;
    }
    
    // Ensure menus array is attached to the user object!
    if (data?.menus && !userData.menus) {
      userData.menus = data.menus;
    }

    const resolvedUserData = applyUserAccessOverride(userData);
    
    const derivedName = resolvedUserData.full_name || resolvedUserData.name || [resolvedUserData.first_name, resolvedUserData.last_name].filter(Boolean).join(" ") || resolvedUserData.username || "";
    if (derivedName) {
      resolvedUserData.full_name = derivedName;
    }

    console.log('USER DATA WITH PERMS:', userData);

    // SAVE USER
    storage.setItem('userId', resolvedUserData?.id || '');
    storage.setItem('user', JSON.stringify(resolvedUserData));
    storage.setItem('full_name', resolvedUserData?.full_name || '');
    storage.setItem('email', resolvedUserData?.email || '');

    // SAVE ROLE
    storage.setItem('role', resolvedUserData?.role || resolvedUserData?.role_name || '');

    // UPDATE STATE
    setUser(resolvedUserData);
    setIsLoggedIn(true);

    console.log('STORED USER:', resolvedUserData);
  };

  // =========================
  // UPDATE USER
  // =========================

  const updateStoredUser = (nextUser) => {
    if (!nextUser) {
      setUser(null);
      return;
    }
    
    const derivedName = nextUser.full_name || nextUser.name || [nextUser.first_name, nextUser.last_name].filter(Boolean).join(" ") || nextUser.username || "";
    if (derivedName) {
      nextUser.full_name = derivedName;
    }

    setUser(nextUser);

    const isLocal = !!localStorage.getItem('token');
    const storage = isLocal ? localStorage : sessionStorage;

    storage.setItem('userId', nextUser.id);
    storage.setItem('user', JSON.stringify(nextUser));
    storage.setItem('full_name', nextUser.full_name || '');
    storage.setItem('email', nextUser.email || '');
    storage.setItem('username', nextUser.username || '');
    storage.setItem('role', nextUser.role || nextUser.role_name || '');
  };

  // =========================
  // LOGOUT
  // =========================

  const logout = () => {
    ['token', 'userId', 'user', 'full_name', 'email', 'role', 'clinic_setup_completed'].forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    setIsLoggedIn(false);
    setUser(null);
  };

  // =========================
  // LOADING
  // =========================

  if (!isInitialized) {

    return null;
  }

  // =========================
  // PROVIDER
  // =========================

  return (

    <AuthContext.Provider
      value={{
        isLoggedIn,
        user,
        login,
        logout,
        updateStoredUser
      }}
    >

      {children}

    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node
};
